import GuiContext from "config/context/guiContext";
import { useContext, useRef, useState } from "react";

/**
 * Uklanjanje baze — stvarno, ne skidanje sa liste.
 *
 * Zatečeno „Remove Database" je zvalo samo `/api/inerygui/unlink_database`, što
 * bazu briše iz KORISNIKOVE liste u GUI-ju. Na lancu je ostajalo sve: podaci,
 * kompozicije, kontrakt. Poruka je pri tom glasila „Database is removed", pa je
 * korisnik verovao da je obrisao nešto što i dalje postoji — i za šta se i dalje
 * troši memorija naloga.
 *
 * Pravi postupak ima tri koraka i redosled NIJE proizvoljan:
 *
 *   1. PRAŽNJENJE  `/api/database/clear`   po kompoziciji, u više poziva
 *   2. UKLANJANJE  `/api/database/drop`    kompozicija nestaje iz kontrakta
 *   3. SKIDANJE    `/api/inerygui/unlink_database`   samo iz liste u GUI-ju
 *
 * Prvo pražnjenje, pa uklanjanje. Obrnuto ostavlja redove u bazi stanja kojima
 * više ne postoji definicija tabele — dakle zauzimaju memoriju, a ne mogu se ni
 * pročitati ni obrisati.
 *
 * Zašto je pražnjenje petlja: brisanje ide kroz akciju kontrakta koja radi u
 * paketima i vraća „ima još". Backend po zahtevu potroši ograničen broj poziva
 * i vrati `cursor`; petlju vodi ova strana, da napredak bude vidljiv i da
 * prekid bude pauza a ne gubitak. Vidi `clearComposition.js`.
 */
/** Koliko puta se uzastopni neuspeh pražnjenja ponavlja pre odustajanja. */
const MAX_CLEAR_RETRIES = 5;

export const useRemoveDatabase = () => {
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    // Blok u kom je kontrakt skinut. Do sada se odgovor `destroy` rute bacao, pa
    // se „obrisano" izgovaralo nad blokom koji pri račvi još može da otpadne.
    // Prati se samo brisanje — ono je nepovratno po nameri.
    const [destroyedInBlock, setDestroyedInBlock] = useState(null);
    // Šta je SERVER rekao o ishodu. Do 02.09. se odgovor `/destroy` koristio
    // samo pri neuspehu, a pri uspehu bacao — pa je dijalog ispisivao svoj
    // sopstveni naslov („qatest1 is deleted"), a serverova poruka o skinutim
    // ključevima nije stizala nikuda. Kad radnja odustane od nečega, to mora
    // da se vidi.
    const [outcome, setOutcome] = useState(null);
    /** @type {[{phase: string, composition: string, index: number, total: number, passes: number}|null, Function]} */
    const [progress, setProgress] = useState(null);

    const cancelled = useRef(false);
    const { selected: { selectedServer }, userData, URL } = useContext(GuiContext);

    const post = async (path, body) => {
        const res = await fetch(`${URL}${path}`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        return res.json();
    };

    const cancel = () => { cancelled.current = true; };

    /**
     * @param {{database: string, privateKey: string, permission?: string, destroy?: boolean}} args
     *   `destroy` dodaje jedan nivo dublje: posle pražnjenja i uklanjanja
     *   kompozicija skida i sam kontrakt sa naloga. Nije zaseban tok nego
     *   nastavak ovog, jer redosled `clear` → `drop` → `destroy` nije stvar
     *   izbora — obrnuto ostavlja redove bez definicije tabele.
     */
    const removeDatabase = async ({ database, privateKey, permission = "active", destroy = false, unlink = false, keepStructure = false, listOnly = false }) => {
        cancelled.current = false;
        setRunning(true);
        setError(null);
        setDone(false);
        setDestroyedInBlock(null);
        setOutcome(null);

        try {
            const base = { server_url: selectedServer, database, private_key: privateKey, permission };

            const structure = await post("/api/database/structure", {
                server_url: selectedServer,
                database,
            });
            if (structure?.status === false) {
                throw new Error(structure.message || "Could not read the database structure.");
            }

            const compositions = (structure.compositions || structure.data?.compositions || [])
                .map((c) => c.name)
                .filter(Boolean);

            // `listOnly` ne dira lanac uopšte — ni pražnjenje, ni uklanjanje
            // kompozicija, ni kontrakt. Menja se samo spisak.
            for (let i = 0; listOnly ? false : i < compositions.length; i++) {
                const composition = compositions[i];
                let cursor = null;
                let passes = 0;
                let retries = 0;

                // 1. Pražnjenje — ponavlja se dok backend ne javi `done`.
                do {
                    if (cancelled.current) throw new Error("Stopped at the user's request.");

                    setProgress({ phase: "clearing", composition, index: i, total: compositions.length, passes });
                    const res = await post("/api/database/clear", { ...base, composition, cursor });

                    // Neuspeo prolaz NIJE kraj. `clearComposition` uz grešku vraća
                    // kursor, dakle pozicija je poznata i sledeći poziv nastavlja
                    // odatle. Ranije je jedan prolazni neuspeh na lancu — granica
                    // CPU-a po transakciji, trenutno opterećenje čvora — rušio celo
                    // brisanje, iako bi ponovljen poziv prošao. Izmereno 25.08. na
                    // `deltest1`: pad, pa isti poziv iz prve dovrši ostatak.
                    //
                    // Bez kursora se ipak staje: tada se ne zna gde smo, a slepo
                    // ponavljanje bi vrtelo isti neuspeh.
                    if (res?.status === false) {
                        const next = res.data?.cursor;
                        if (!next || retries >= MAX_CLEAR_RETRIES) {
                            throw new Error(res.message || res.error);
                        }
                        retries++;
                        cursor = next;
                        continue;
                    }
                    retries = 0;

                    cursor = res.data?.done ? null : res.data?.cursor;
                    passes++;
                    // Bez ovoga bi greška u kursoru davala beskonačnu petlju u
                    // pregledaču; radije stani i reci.
                    if (passes > 10000) throw new Error(`Clearing '${composition}' is making no progress.`);
                } while (cursor);

                // 2. Uklanjanje kompozicije — tek kad je prazna, i samo ako se
                // struktura ne zadržava.
                //
                // „Isprazni podatke, zadrži tabele" je zaseban i legitiman
                // ishod: uvoz nije doneo ono što treba, definicije jesu dobre.
                // Tabela na lancu pri tom NE zauzima ništa — nastaje prvim
                // upisom, a nestaje brisanjem poslednjeg reda.
                if (keepStructure) continue;

                // Kad se rusi CELA kuca, ne prezidava se svaka soba pre toga.
                //
                // `dropComposition` uklanja jednu kompoziciju tako sto REGENERISE
                // i ponovo prevede CEO ugovor — jedno `inery-cpp` po kompoziciji.
                // Za bazu sa N tabela to je N prevodjenja, a svako od njih pravi
                // ugovor koji ce za koji trenutak biti obrisan u celini.
                //
                // `destroyDatabase` na serveru vec radi sve sto ovde treba:
                // isprazni svaku kompoziciju, obrise zapise o strukturi
                // (`cldbstruct`, `clearschema`) i skine kod (`setabi`/`setcode`
                // prazni). Jedino sto `drop` ima a `destroy` nema jeste poziv
                // `rmperm` — a te akcije NA UGOVORU NEMA (provereno u ABI-ju
                // 02.09.2026), pa on uvek baca i biva progutan u svom `catch`.
                //
                // Praznjenje IZNAD ostaje: ono daje napredak po kompoziciji i
                // deli posao na vise zahteva, pa ogromna baza ne visi o jednom.
                if (destroy) continue;

                if (cancelled.current) throw new Error("Stopped at the user's request.");
                setProgress({ phase: "dropping", composition, index: i, total: compositions.length, passes });
                const dropped = await post("/api/database/drop", { ...base, composition });
                if (dropped?.status === false) throw new Error(dropped.message || dropped.error);
            }

            // 3. Skidanje kontrakta, ako je traženo. Tek sada — kad iza koda
            //    više nema redova kojima bi ostala definicija tabele.
            if (destroy && !listOnly) {
                if (cancelled.current) throw new Error("Stopped at the user's request.");
                setProgress({ phase: "destroying", composition: "", index: compositions.length, total: compositions.length, passes: 0 });
                const destroyed = await post("/api/database/destroy", {
                    server_url: selectedServer,
                    database,
                    private_key: privateKey,
                    permission,
                });
                if (destroyed?.status === false) throw new Error(destroyed.message || destroyed.error);
                // `null` kad kontrakta nije ni bilo — tada nema šta da postane
                // konačno, i prikaz to razlikuje od „još čekam".
                setDestroyedInBlock(destroyed?.data?.block_num ?? null);
                setOutcome(destroyed?.message || null);
            }

            // 4. Skidanje sa liste — SAMO ako je traženo.
            //
            // Zatečeni kvar je bio skidanje BEZ brisanja: baza nestane iz GUI-ja
            // a na lancu ostane i troši memoriju. Popravka je te dve radnje
            // spojila, pa se nepovratna izvodila zajedno sa bezopasnom — i brana
            // `accounts.length <= 1` je čuvala pogrešnu od njih dve.
            //
            // Ispravna asimetrija: skidanje bez brisanja se NE dozvoljava,
            // brisanje bez skidanja se dozvoljava. Bez toga se jedina baza nije
            // mogla obrisati, iako je prazna baza u spisku normalno stanje —
            // tako svaki nalog i počinje.
            if (unlink) {
                setProgress({ phase: "unlinking", composition: "", index: compositions.length, total: compositions.length, passes: 0 });
                // `private_key` je OBAVEZAN — kontroler njime proverava da ključ
                // zaista pripada nalogu („Wrong private key for account …"). Ovde
                // ga nije bilo, pa je korak uvek padao na „Validation failed", i
                // to tek pošto je baza već obrisana. `account_id` se ne traži.
                const removed = await post("/api/inerygui/unlink_database", {
                    server_url: selectedServer,
                    guiname: userData?.guiname,
                    account_name: database,
                    private_key: privateKey,
                });
                if (removed?.status === false) throw new Error(removed.message || removed.error);
            }

            setProgress({ phase: "finished", composition: "", index: compositions.length, total: compositions.length, passes: 0 });
            setDone(true);
        } catch (err) {
            setError(err.message || "Removing the database failed.");
        } finally {
            setRunning(false);
        }
    };

    const reset = () => {
        setRunning(false);
        setError(null);
        setDone(false);
        setProgress(null);
        setDestroyedInBlock(null);
        setOutcome(null);
        cancelled.current = false;
    };

    return { removeDatabase, cancel, reset, running, error, done, progress, destroyedInBlock, outcome };
};
